import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

// ── Per-floor architectural specs ─────────────────────────────────────────────
// Each floor has its own dimensions, style, and room layout
const SPECS = [
  { id:0, w:18, d:15, h:4.4, roomOff:3.2, tileS:3.8, colS:0.52, colT:'rect',  style:'lobby'   },
  { id:1, w:15, d:13, h:4.0, roomOff:2.8, tileS:3.2, colS:0.32, colT:'rect',  style:'office'  },
  { id:2, w:13, d:13, h:3.8, roomOff:2.4, tileS:2.9, colS:0.28, colT:'round', style:'studio'  },
  { id:3, w:13, d:11, h:3.6, roomOff:2.1, tileS:2.5, colS:0.28, colT:'rect',  style:'lab'     },
  { id:4, w:11, d:11, h:3.4, roomOff:1.9, tileS:2.3, colS:0.22, colT:'round', style:'rooftop' },
];
// Cumulative base Y
let _cy = 0; SPECS.forEach(s => { s.baseY = _cy; _cy += s.h; });
const TOTAL_H = _cy; // ~19.2 units

// ── Floor / Room data ─────────────────────────────────────────────────────────
const FLOORS = [
  {
    id:0, name:'Ground Floor', color:0x38bdf8,
    rooms:[
      { id:0, name:'Reception',   pos:[-3.2,-3.2], color:0x38bdf8, desc:'Main entrance and visitor management area.',        items:['Info Desk','Lounge Seats','Welcome Screen'] },
      { id:1, name:'Server Room', pos:[ 3.2,-3.2], color:0x06b6d4, desc:'Core infrastructure and database servers.',         items:['Server Racks','Cooling Unit','UPS Battery'] },
      { id:2, name:'Security',    pos:[-3.2, 3.2], color:0x0ea5e9, desc:'Monitoring and access control operations.',         items:['CCTV Station','Access Control','Security Desk'] },
      { id:3, name:'Lobby Hall',  pos:[ 3.2, 3.2], color:0x7dd3fc, desc:'Elevators and stairs connecting all floors.',      items:['Elevators','Stairs','Floor Directory'] },
    ],
  },
  {
    id:1, name:'1st Floor', color:0x22d3ee,
    rooms:[
      { id:0, name:'Dev Office A', pos:[-2.8,-2.8], color:0x22d3ee, desc:'Backend engineering team workspace.',             items:['Workstations','Whiteboard','Standing Desks'] },
      { id:1, name:'Dev Office B', pos:[ 2.8,-2.8], color:0x0891b2, desc:'Infrastructure and DevOps team workspace.',       items:['Dual Monitors','Pipeline Board','Server KVM'] },
      { id:2, name:'Meeting Room', pos:[-2.8, 2.8], color:0x67e8f9, desc:'Sprint planning and technical discussions.',      items:['Conference Table','Projector','Agile Board'] },
      { id:3, name:'Break Room',   pos:[ 2.8, 2.8], color:0xa5f3fc, desc:'Relax and recharge between coding sessions.',    items:['Coffee Machine','Bean Bags','Snack Bar'] },
    ],
  },
  {
    id:2, name:'2nd Floor', color:0x818cf8,
    rooms:[
      { id:0, name:'Design Studio', pos:[-2.4,-2.4], color:0x818cf8, desc:'UI/UX design and prototyping hub.',             items:['Drawing Tablets','Color Boards','Mood Wall'] },
      { id:1, name:'UI Lab',        pos:[ 2.4,-2.4], color:0x6366f1, desc:'Component testing and accessibility reviews.',  items:['Test Devices','Figma Stations','Pattern Lib'] },
      { id:2, name:'Presentation',  pos:[-2.4, 2.4], color:0xa5b4fc, desc:'Client demos and design review sessions.',      items:['Large Display','Stage Area','Feedback Wall'] },
      { id:3, name:'Inspiration',   pos:[ 2.4, 2.4], color:0xc7d2fe, desc:'Creative space for brainstorming new ideas.',  items:['Art Wall','Sketch Tables','Ref Library'] },
    ],
  },
  {
    id:3, name:'3rd Floor', color:0x34d399,
    rooms:[
      { id:0, name:'ML Lab',       pos:[-2.1,-2.1], color:0x34d399, desc:'Machine learning training and evaluation.',      items:['GPU Workstations','Training Monitor','Data Boards'] },
      { id:1, name:'GPU Cluster',  pos:[ 2.1,-2.1], color:0x10b981, desc:'High-performance compute for AI workloads.',     items:['GPU Racks','Cooling Systems','Net Switch'] },
      { id:2, name:'Research Lab', pos:[-2.1, 2.1], color:0x6ee7b7, desc:'Experimental technology and R&D projects.',     items:['Experiment Rigs','Notebooks','Sensor Array'] },
      { id:3, name:'Collab Zone',  pos:[ 2.1, 2.1], color:0xa7f3d0, desc:'Cross-team collaboration and knowledge sharing.',items:['Roundtable','Knowledge Wall','Stream Setup'] },
    ],
  },
  {
    id:4, name:'Rooftop', color:0xf472b6,
    rooms:[
      { id:0, name:'Sky Lounge',   pos:[-1.9,-1.9], color:0xf472b6, desc:'Open-air relaxation with panoramic city views.', items:['Deck Chairs','Bar Counter','City View'] },
      { id:1, name:'Observatory',  pos:[ 1.9,-1.9], color:0xec4899, desc:'Telescope array and stargazing platform.',       items:['Telescope','Star Charts','Night Vision'] },
      { id:2, name:'Helipad',      pos:[-1.9, 1.9], color:0xfb7185, desc:'Emergency and executive transport landing zone.',items:['Landing Lights','Wind Sock','Safety Kit'] },
      { id:3, name:'Antenna Farm', pos:[ 1.9, 1.9], color:0xfda4af, desc:'Communication arrays and network infrastructure.',items:['5G Antenna','Satellite Dish','Signal Mon'] },
    ],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const SH  = 0.24; // slab height
const PH  = 1.5;  // partition height
const PT  = 0.09; // partition thickness
const WT  = 0.12; // wall thickness

function toHex(c) { return `#${c.toString(16).padStart(6, '0')}`; }

function makeLabel(text, color) {
  const cv = document.createElement('canvas');
  cv.width = 256; cv.height = 52;
  const ctx = cv.getContext('2d');
  ctx.fillStyle = 'rgba(5,7,12,0.86)';
  ctx.beginPath(); ctx.roundRect(0, 0, 256, 52, 6); ctx.fill();
  ctx.fillStyle = toHex(color);
  ctx.font = 'bold 19px sans-serif';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(text, 128, 26);
  const spr = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(cv), transparent: true, depthTest: false })
  );
  spr.scale.set(2.1, 0.46, 1);
  return spr;
}

function buildFurniture(room) {
  const g  = new THREE.Group();
  const rc = new THREE.Color(room.color);
  const bm = new THREE.MeshLambertMaterial({ color: rc.clone().multiplyScalar(0.45) });
  const gm = new THREE.MeshStandardMaterial({ color: room.color, emissive: room.color, emissiveIntensity: 0.45 });

  // Desk + legs + monitor + chair (every room)
  const desk = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.08, 0.6), bm);
  desk.position.set(0, SH / 2 + 0.72, 0.3); g.add(desk);
  [[-0.55,-0.26],[0.55,-0.26],[-0.55,0.26],[0.55,0.26]].forEach(([dx,dz]) => {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.62, 0.06), bm);
    leg.position.set(dx, SH / 2 + 0.36, 0.3 + dz); g.add(leg);
  });
  const mon = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.36, 0.04), gm);
  mon.position.set(0, SH / 2 + 1.02, 0.12); g.add(mon);
  const ch = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.06, 0.48), bm);
  ch.position.set(0, SH / 2 + 0.5, 0.9); g.add(ch);

  const n = room.name;

  if (n.includes('Server') || n.includes('GPU') || n.includes('Cluster')) {
    const rm = new THREE.MeshLambertMaterial({ color: 0x111825 });
    const lm = new THREE.MeshStandardMaterial({ color: room.color, emissive: room.color, emissiveIntensity: 2 });
    for (let r = 0; r < 2; r++) {
      const rack = new THREE.Mesh(new THREE.BoxGeometry(0.38, 1.75, 0.52), rm);
      rack.position.set(-0.65 + r * 1.3, SH / 2 + 0.875, -0.65); g.add(rack);
      for (let l = 0; l < 5; l++) {
        const led = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.022, 0.022), lm);
        led.position.set(-0.65 + r * 1.3, SH / 2 + 0.35 + l * 0.28, -0.4); g.add(led);
      }
    }
  }
  if (n.includes('Meeting') || n.includes('Collab') || n.includes('Lobby Hall')) {
    const top = new THREE.Mesh(new THREE.CylinderGeometry(0.65, 0.65, 0.06, 10), bm);
    top.position.set(-0.2, SH / 2 + 0.72, -0.3); g.add(top);
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2;
      const c2 = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.05, 0.38), bm);
      c2.position.set(-0.2 + Math.cos(a) * 0.9, SH / 2 + 0.49, -0.3 + Math.sin(a) * 0.9); g.add(c2);
    }
  }
  if (n.includes('Lounge') || n.includes('Break') || n.includes('Inspiration') || n.includes('Sky')) {
    const sofa = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.28, 0.5), bm);
    sofa.position.set(-0.2, SH / 2 + 0.38, -0.65); g.add(sofa);
    const ct = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.28, 0.05, 8), bm);
    ct.position.set(-0.2, SH / 2 + 0.52, -0.1); g.add(ct);
  }
  if (n.includes('Reception') || n.includes('Security') || n.includes('Lobby Hall')) {
    const cm = new THREE.MeshLambertMaterial({ color: rc.clone().multiplyScalar(0.38) });
    const ctr = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.88, 0.28), cm);
    ctr.position.set(0, SH / 2 + 0.44, -0.55); g.add(ctr);
    const scr = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.34, 0.04), gm);
    scr.position.set(0, SH / 2 + 1.12, -0.4); g.add(scr);
  }
  if (n.includes('Observatory')) {
    const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.12, 0.9, 12), bm);
    tube.rotation.z = Math.PI / 5; tube.position.set(0.2, SH / 2 + 0.85, -0.3); g.add(tube);
  }
  // Plant (every room)
  const pm = new THREE.MeshLambertMaterial({ color: 0x16a34a });
  const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.38, 6), pm);
  stem.position.set(-0.88, SH / 2 + 0.49, -0.88); g.add(stem);
  const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.19, 6, 6), pm);
  leaf.position.set(-0.88, SH / 2 + 0.84, -0.88); g.add(leaf);
  return g;
}

// ── Style-specific structure builders ────────────────────────────────────────

function addLobby(grp, { w, d, colS }, colH, wallY, fc) {
  // Solid concrete skirt (lower 1.1 units) + glass above
  const skirtH = 1.1;
  const skirtMat = new THREE.MeshStandardMaterial({ color: 0x1a1f2a, roughness: 0.9, metalness: 0.1 });
  const glassMat = new THREE.MeshStandardMaterial({ color: fc, roughness: 0.05, metalness: 0.9, transparent: true, opacity: 0.24, side: THREE.DoubleSide });
  const glassH = colH - skirtH;
  const glassY = SH / 2 + skirtH + glassH / 2;

  [
    { s:[w-colS, skirtH, WT], p:[0, SH/2+skirtH/2,  d/2-WT/2] },
    { s:[w-colS, skirtH, WT], p:[0, SH/2+skirtH/2, -d/2+WT/2] },
    { s:[WT, skirtH, d-colS], p:[ w/2-WT/2, SH/2+skirtH/2, 0] },
    { s:[WT, skirtH, d-colS], p:[-w/2+WT/2, SH/2+skirtH/2, 0] },
  ].forEach(({s, p}) => { const m = new THREE.Mesh(new THREE.BoxGeometry(...s), skirtMat); m.position.set(...p); grp.add(m); });

  [
    { s:[w-colS, glassH, WT], p:[0, glassY,  d/2-WT/2] },
    { s:[w-colS, glassH, WT], p:[0, glassY, -d/2+WT/2] },
    { s:[WT, glassH, d-colS], p:[ w/2-WT/2, glassY, 0] },
    { s:[WT, glassH, d-colS], p:[-w/2+WT/2, glassY, 0] },
  ].forEach(({s, p}) => { const m = new THREE.Mesh(new THREE.BoxGeometry(...s), glassMat.clone()); m.position.set(...p); grp.add(m); });

  // Mid-span columns on long (x) faces
  const colMat = new THREE.MeshStandardMaterial({ color: 0x1a2a3e, roughness: 0.6, metalness: 0.5 });
  const midGeo = new THREE.BoxGeometry(colS * 0.75, colH, colS * 0.75);
  [d / 2 - colS / 2, -(d / 2 - colS / 2)].forEach(z => {
    const col = new THREE.Mesh(midGeo, colMat); col.position.set(0, wallY, z); grp.add(col);
  });

  // X-brace on front and back facades
  const braceLen = Math.sqrt(w * w + colH * colH);
  const braceA   = Math.atan2(colH, w);
  const braceMat = new THREE.MeshStandardMaterial({ color: 0x2a3c54, roughness: 0.4, metalness: 0.75 });
  [d / 2 - WT / 2, -(d / 2 - WT / 2)].forEach(z => {
    [-braceA, braceA].forEach(a => {
      const b = new THREE.Mesh(new THREE.BoxGeometry(braceLen, 0.08, 0.08), braceMat);
      b.position.set(0, wallY, z); b.rotation.z = a; grp.add(b);
    });
  });
}

function addOffice(grp, { w, d, colS }, colH, wallY, fc) {
  // Dark glass walls
  const glassMat = new THREE.MeshStandardMaterial({ color: fc, roughness: 0.05, metalness: 0.9, transparent: true, opacity: 0.2, side: THREE.DoubleSide });
  [
    { s:[w-colS, colH, WT], p:[0, wallY,  d/2-WT/2] },
    { s:[w-colS, colH, WT], p:[0, wallY, -d/2+WT/2] },
    { s:[WT, colH, d-colS], p:[ w/2-WT/2, wallY, 0] },
    { s:[WT, colH, d-colS], p:[-w/2+WT/2, wallY, 0] },
  ].forEach(({s, p}) => { const m = new THREE.Mesh(new THREE.BoxGeometry(...s), glassMat.clone()); m.position.set(...p); grp.add(m); });

  // Horizontal metallic louvers (5 rows, all 4 faces)
  const louverMat = new THREE.MeshStandardMaterial({ color: 0x2a3d54, roughness: 0.3, metalness: 0.9 });
  for (let i = 0; i < 5; i++) {
    const lY = SH / 2 + (i + 0.5) * (colH / 5);
    [
      { s:[w-colS*2, 0.055, 0.14], p:[0, lY,  d/2+0.07] },
      { s:[w-colS*2, 0.055, 0.14], p:[0, lY, -(d/2+0.07)] },
      { s:[0.14, 0.055, d-colS*2], p:[ w/2+0.07, lY, 0] },
      { s:[0.14, 0.055, d-colS*2], p:[-(w/2+0.07), lY, 0] },
    ].forEach(({s, p}) => { const m = new THREE.Mesh(new THREE.BoxGeometry(...s), louverMat); m.position.set(...p); grp.add(m); });
  }

  // Diagonal X-braces on left and right faces (short d-faces)
  const dSpan    = d - colS;
  const braceLen = Math.sqrt(colH * colH + dSpan * dSpan);
  const braceA   = Math.atan2(dSpan, colH);
  const braceMat = new THREE.MeshStandardMaterial({ color: 0x1e3050, roughness: 0.3, metalness: 0.9 });
  [w / 2 - colS / 2, -(w / 2 - colS / 2)].forEach(x => {
    [-braceA, braceA].forEach(a => {
      const b = new THREE.Mesh(new THREE.BoxGeometry(0.06, braceLen, 0.06), braceMat);
      b.position.set(x, wallY, 0); b.rotation.x = a; grp.add(b);
    });
  });
}

function addStudio(grp, { w, d, colS }, colH, wallY) {
  // Light clean glass
  const glassMat = new THREE.MeshStandardMaterial({ color: 0xd4e8f8, roughness: 0.05, metalness: 0.7, transparent: true, opacity: 0.22, side: THREE.DoubleSide });
  [
    { s:[w-colS, colH, WT], p:[0, wallY,  d/2-WT/2] },
    { s:[w-colS, colH, WT], p:[0, wallY, -d/2+WT/2] },
    { s:[WT, colH, d-colS], p:[ w/2-WT/2, wallY, 0] },
    { s:[WT, colH, d-colS], p:[-w/2+WT/2, wallY, 0] },
  ].forEach(({s, p}) => { const m = new THREE.Mesh(new THREE.BoxGeometry(...s), glassMat.clone()); m.position.set(...p); grp.add(m); });

  // Vertical fins on all 4 facades
  const finMat    = new THREE.MeshStandardMaterial({ color: 0xbbd0e8, roughness: 0.2, metalness: 0.85 });
  const spacing   = 0.88;
  const frontSpan = w - colS;
  const sideSpan  = d - colS;
  const nFx = Math.floor(frontSpan / spacing);
  const nFz = Math.floor(sideSpan  / spacing);
  const projOut = 0.18;

  for (let i = 0; i <= nFx; i++) {
    const fx = -(w / 2 - colS / 2) + i * (frontSpan / nFx);
    [d / 2 + projOut, -(d / 2 + projOut)].forEach(fz => {
      const fin = new THREE.Mesh(new THREE.BoxGeometry(0.05, colH, projOut * 2), finMat);
      fin.position.set(fx, wallY, fz); grp.add(fin);
    });
  }
  for (let i = 0; i <= nFz; i++) {
    const fz = -(d / 2 - colS / 2) + i * (sideSpan / nFz);
    [w / 2 + projOut, -(w / 2 + projOut)].forEach(fx => {
      const fin = new THREE.Mesh(new THREE.BoxGeometry(projOut * 2, colH, 0.05), finMat);
      fin.position.set(fx, wallY, fz); grp.add(fin);
    });
  }
}

function addLab(grp, { w, d, colS }, colH, wallY, fc) {
  // Dark tinted glass
  const glassMat = new THREE.MeshStandardMaterial({ color: 0x08121c, roughness: 0.05, metalness: 0.95, transparent: true, opacity: 0.38, side: THREE.DoubleSide });
  [
    { s:[w-colS, colH, WT], p:[0, wallY,  d/2-WT/2] },
    { s:[w-colS, colH, WT], p:[0, wallY, -d/2+WT/2] },
    { s:[WT, colH, d-colS], p:[ w/2-WT/2, wallY, 0] },
    { s:[WT, colH, d-colS], p:[-w/2+WT/2, wallY, 0] },
  ].forEach(({s, p}) => { const m = new THREE.Mesh(new THREE.BoxGeometry(...s), glassMat.clone()); m.position.set(...p); grp.add(m); });

  // Bay windows — one centered on each of the 4 faces
  const bayMat  = new THREE.MeshStandardMaterial({ color: fc, roughness: 0.05, metalness: 0.9, transparent: true, opacity: 0.38, side: THREE.DoubleSide });
  const bayH    = colH * 0.55;
  const bayProj = 0.48;
  const bayW    = 2.2;
  const bayOffY = wallY + colH * 0.05;
  [
    { p:[0,  bayOffY,  d/2+bayProj/2], s:[bayW, bayH, bayProj] },
    { p:[0,  bayOffY, -(d/2+bayProj/2)], s:[bayW, bayH, bayProj] },
    { p:[ w/2+bayProj/2, bayOffY, 0], s:[bayProj, bayH, bayW] },
    { p:[-(w/2+bayProj/2), bayOffY, 0], s:[bayProj, bayH, bayW] },
  ].forEach(({p, s}) => { const m = new THREE.Mesh(new THREE.BoxGeometry(...s), bayMat.clone()); m.position.set(...p); grp.add(m); });

  // Glowing mid-height ring
  const fc2 = new THREE.Color(fc);
  const ringMat = new THREE.MeshStandardMaterial({ color: fc2, emissive: fc2, emissiveIntensity: 0.7 });
  const ringY   = SH / 2 + colH * 0.48;
  [
    { s:[w+0.12, 0.055, WT], p:[0, ringY,  d/2] },
    { s:[w+0.12, 0.055, WT], p:[0, ringY, -d/2] },
    { s:[WT, 0.055, d+0.12], p:[ w/2, ringY, 0] },
    { s:[WT, 0.055, d+0.12], p:[-w/2, ringY, 0] },
  ].forEach(({s, p}) => { const m = new THREE.Mesh(new THREE.BoxGeometry(...s), ringMat); m.position.set(...p); grp.add(m); });
}

function addRooftop(grp, { w, d, colS }, colH) {
  // Low railing (0.95 units) instead of full walls
  const railMat = new THREE.MeshStandardMaterial({ color: 0x8be4ff, roughness: 0.05, metalness: 0.9, transparent: true, opacity: 0.28, side: THREE.DoubleSide });
  const railH = 0.95;
  [
    { s:[w-colS, railH, WT], p:[0, SH/2+railH/2,  d/2-WT/2] },
    { s:[w-colS, railH, WT], p:[0, SH/2+railH/2, -d/2+WT/2] },
    { s:[WT, railH, d-colS], p:[ w/2-WT/2, SH/2+railH/2, 0] },
    { s:[WT, railH, d-colS], p:[-w/2+WT/2, SH/2+railH/2, 0] },
  ].forEach(({s, p}) => { const m = new THREE.Mesh(new THREE.BoxGeometry(...s), railMat.clone()); m.position.set(...p); grp.add(m); });

  // Pergola beams across the top
  const beamMat = new THREE.MeshStandardMaterial({ color: 0x1a2535, roughness: 0.5, metalness: 0.85 });
  const beamY   = SH / 2 + colH - 0.06;
  for (let i = 0; i <= 4; i++) {
    const z = -(d / 2 - colS / 2) + i * ((d - colS) / 4);
    const b = new THREE.Mesh(new THREE.BoxGeometry(w - colS, 0.1, 0.1), beamMat);
    b.position.set(0, beamY, z); grp.add(b);
  }
  for (let i = 0; i <= 3; i++) {
    const x = -(w / 2 - colS / 2) + i * ((w - colS) / 3);
    const b = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.1, d - colS), beamMat);
    b.position.set(x, beamY - 0.05, 0); grp.add(b);
  }
}

// ── Build one floor ───────────────────────────────────────────────────────────
function buildFloor(floorData, floorMeshes, roomMeshes) {
  const spec = SPECS[floorData.id];
  const { w, d, h, colS, colT, style, baseY, roomOff, tileS } = spec;
  const colH  = h - SH;
  const wallY = SH / 2 + colH / 2;
  const fc    = new THREE.Color(floorData.color);

  const grp = new THREE.Group();
  grp.position.y = baseY;
  grp.userData   = { originalY: baseY, floorId: floorData.id };

  // Slab (studio gets cantilevered slab: +0.8 on each side)
  const slabEx = style === 'studio' ? 0.8 : 0;
  const slab   = new THREE.Mesh(
    new THREE.BoxGeometry(w + slabEx, SH, d + slabEx),
    new THREE.MeshStandardMaterial({ color: 0x0f1720, roughness: 0.8, metalness: 0.2 })
  );
  slab.userData = { floorId: floorData.id, clickType: 'floor' };
  grp.add(slab); floorMeshes.push(slab);

  // Glowing edge strip
  const strip = new THREE.Mesh(
    new THREE.BoxGeometry(w + slabEx + 0.08, 0.055, d + slabEx + 0.08),
    new THREE.MeshStandardMaterial({ color: fc, emissive: fc, emissiveIntensity: 0.88, roughness: 0.2 })
  );
  strip.position.y = SH / 2 + 0.027; grp.add(strip);

  // Columns
  const colMat = new THREE.MeshStandardMaterial({ color: 0x1a2a3e, roughness: 0.6, metalness: 0.5 });
  const hw = w / 2 - colS / 2;
  const hd = d / 2 - colS / 2;
  [[-hw,-hd],[hw,-hd],[-hw,hd],[hw,hd]].forEach(([x, z]) => {
    const col = colT === 'round'
      ? new THREE.Mesh(new THREE.CylinderGeometry(colS / 2, colS / 2, colH, 8), colMat)
      : new THREE.Mesh(new THREE.BoxGeometry(colS, colH, colS), colMat);
    col.position.set(x, wallY, z);
    col.userData = { floorId: floorData.id, clickType: 'floor' };
    grp.add(col); floorMeshes.push(col);
  });

  // Style-specific walls
  if (style === 'lobby')   addLobby  (grp, spec, colH, wallY, floorData.color);
  if (style === 'office')  addOffice (grp, spec, colH, wallY, floorData.color);
  if (style === 'studio')  addStudio (grp, spec, colH, wallY);
  if (style === 'lab')     addLab    (grp, spec, colH, wallY, floorData.color);
  if (style === 'rooftop') addRooftop(grp, spec, colH);

  // Internal partition walls (x=0 and z=0 dividers with door gaps)
  const partMat  = new THREE.MeshStandardMaterial({ color: fc, roughness: 0.35, metalness: 0.55, transparent: true, opacity: 0.35, side: THREE.DoubleSide });
  const halfW    = w / 2 - colS - 0.1;
  const halfD    = d / 2 - colS - 0.1;
  const gap      = 0.52;
  const partY    = SH / 2 + PH / 2;
  const segD     = halfD - gap;
  const segW     = halfW - gap;
  [-1, 1].forEach(s => {
    const pD = new THREE.Mesh(new THREE.BoxGeometry(PT, PH, segD), partMat.clone());
    pD.position.set(0, partY, s * (gap + segD / 2)); grp.add(pD);
    const pW = new THREE.Mesh(new THREE.BoxGeometry(segW, PH, PT), partMat.clone());
    pW.position.set(s * (gap + segW / 2), partY, 0); grp.add(pW);
  });

  // Room tiles + furniture + labels + lights
  floorData.rooms.forEach(room => {
    const [rx, rz] = room.pos;
    const rc = new THREE.Color(room.color);

    const tile = new THREE.Mesh(
      new THREE.BoxGeometry(tileS, 0.04, tileS),
      new THREE.MeshStandardMaterial({ color: rc, emissive: rc, emissiveIntensity: 0.14, roughness: 0.8, transparent: true, opacity: 0.52 })
    );
    tile.position.set(rx, SH / 2 + 0.02, rz);
    tile.userData = { floorId: floorData.id, roomId: room.id, clickType: 'room' };
    grp.add(tile); roomMeshes.push(tile); floorMeshes.push(tile);

    const lbl = makeLabel(room.name, room.color);
    lbl.position.set(rx, h * 0.52, rz);
    lbl.userData = { isLabel: true, floorId: floorData.id };
    grp.add(lbl);

    const fur = buildFurniture(room);
    fur.position.set(rx, 0, rz); grp.add(fur);

    const pl = new THREE.PointLight(room.color, 0.7, 3.5, 2);
    pl.position.set(rx, SH + colH * 0.65, rz); grp.add(pl);
  });

  // Rooftop antenna
  if (style === 'rooftop') {
    const antM = new THREE.MeshLambertMaterial({ color: 0x94a3b8 });
    const ant  = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 3.0, 6), antM);
    ant.position.set(0, SH / 2 + 1.6, 0); grp.add(ant);
    const ball = new THREE.Mesh(new THREE.SphereGeometry(0.14, 12, 12),
      new THREE.MeshStandardMaterial({ color: fc, emissive: fc, emissiveIntensity: 1.6 }));
    ball.position.set(0, SH / 2 + 3.2, 0); grp.add(ball);
  }

  // Interior ambient light (activated when exploded)
  const intLight = new THREE.PointLight(floorData.color, 0, 14, 2);
  intLight.position.set(0, SH + colH / 2, 0); grp.add(intLight);
  grp.userData.light = intLight;

  return grp;
}

// ── Main Component ────────────────────────────────────────────────────────────
const BuildingPage = () => {
  const containerRef  = useRef(null);
  const controlsRef   = useRef(null);
  const camAnimRef    = useRef({ active: false, targetPos: new THREE.Vector3(26, 14, 26), targetLookAt: new THREE.Vector3(0, TOTAL_H / 2, 0) });
  const explodedRef   = useRef(false);
  const modeRef       = useRef('overview');
  const floorIdRef    = useRef(null);
  const enterFloorCb  = useRef(null);
  const enterRoomCb   = useRef(null);

  const [mode,         setMode]         = useState('overview');
  const [currentFloor, setCurrentFloor] = useState(null);
  const [currentRoom,  setCurrentRoom]  = useState(null);
  const [isExploded,   setIsExploded]   = useState(false);
  const [showHint,     setShowHint]     = useState(true);

  const flyTo = useCallback((pos, lookAt) => {
    camAnimRef.current.targetPos.set(...pos);
    camAnimRef.current.targetLookAt.set(...lookAt);
    camAnimRef.current.active = true;
    if (controlsRef.current) controlsRef.current.enabled = false;
  }, []);

  const enterFloor = useCallback((fid) => {
    const floor = FLOORS[fid];
    const spec  = SPECS[fid];
    setCurrentFloor(floor); setCurrentRoom(null);
    setMode('floor'); modeRef.current = 'floor'; floorIdRef.current = fid;
    setIsExploded(false); explodedRef.current = false;
    const midY = spec.baseY + spec.h / 2;
    flyTo([spec.w * 0.85, midY + 3, spec.d * 0.85], [0, midY - 0.5, 0]);
  }, [flyTo]);

  const enterRoom = useCallback((fid, rid) => {
    const spec = SPECS[fid];
    const room = FLOORS[fid].rooms[rid];
    setCurrentRoom(room); setMode('room'); modeRef.current = 'room';
    const [rx, rz] = room.pos;
    flyTo([rx + 3.5, spec.baseY + 2.4, rz + 3.5], [rx, spec.baseY + 1.1, rz]);
  }, [flyTo]);

  const goBack = useCallback(() => {
    if (modeRef.current === 'room') {
      const fid  = floorIdRef.current;
      const spec = SPECS[fid];
      const midY = spec.baseY + spec.h / 2;
      setCurrentRoom(null); setMode('floor'); modeRef.current = 'floor';
      flyTo([spec.w * 0.85, midY + 3, spec.d * 0.85], [0, midY - 0.5, 0]);
    } else if (modeRef.current === 'floor') {
      setCurrentFloor(null); setCurrentRoom(null);
      setMode('overview'); modeRef.current = 'overview'; floorIdRef.current = null;
      flyTo([26, 14, 26], [0, TOTAL_H / 2, 0]);
    }
  }, [flyTo]);

  const toggleExplode = useCallback(() => {
    setIsExploded(v => { explodedRef.current = !v; return !v; });
  }, []);

  enterFloorCb.current = enterFloor;
  enterRoomCb.current  = enterRoom;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const W = container.clientWidth  || 1200;
    const H = container.clientHeight || 700;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x060810);
    scene.fog = new THREE.FogExp2(0x060810, 0.014);

    const camera = new THREE.PerspectiveCamera(40, W / H, 0.1, 300);
    camera.position.set(26, 14, 26);

    const renderer = new THREE.WebGLRenderer({ antialias: window.devicePixelRatio < 2 });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1));
    renderer.toneMapping        = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.15;
    container.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, TOTAL_H / 2, 0);
    controls.enableDamping = true; controls.dampingFactor = 0.055;
    controls.minDistance = 5; controls.maxDistance = 100;
    controls.maxPolarAngle = Math.PI / 2 + 0.06;
    controls.update();
    controlsRef.current = controls;

    // Lighting
    scene.add(new THREE.AmbientLight(0x1a2540, 3.8));
    const sun = new THREE.DirectionalLight(0xfff0d0, 3.2);
    sun.position.set(22, 32, 18);
    scene.add(sun);
    const fill = new THREE.DirectionalLight(0x38bdf8, 0.5);
    fill.position.set(-18, 10, -12); scene.add(fill);

    // Ground
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(120, 120),
      new THREE.MeshLambertMaterial({ color: 0x04060c })
    );
    ground.rotation.x = -Math.PI / 2; ground.position.y = -0.13;
    scene.add(ground);
    scene.add(new THREE.GridHelper(90, 50, 0x38bdf811, 0x38bdf807));

    // Build all floors
    const floorMeshes = [];
    const roomMeshes  = [];
    const floorGroups = FLOORS.map(f => {
      const g = buildFloor(f, floorMeshes, roomMeshes);
      scene.add(g); return g;
    });

    // Raycaster
    const raycaster = new THREE.Raycaster();
    const ptr       = new THREE.Vector2();
    let   ptrDown   = { x: 0, y: 0 };
    const onDown    = e => { ptrDown = { x: e.clientX, y: e.clientY }; };
    const onUp      = e => {
      if (Math.abs(e.clientX - ptrDown.x) > 6 || Math.abs(e.clientY - ptrDown.y) > 6) return;
      const rect = container.getBoundingClientRect();
      ptr.x =  ((e.clientX - rect.left) / rect.width)  * 2 - 1;
      ptr.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1;
      raycaster.setFromCamera(ptr, camera);

      if (modeRef.current === 'overview') {
        const hits = raycaster.intersectObjects(floorMeshes, false);
        if (hits.length > 0) {
          const fid = hits[0].object.userData.floorId;
          if (fid !== undefined) enterFloorCb.current(fid);
        }
      } else if (modeRef.current === 'floor') {
        const fid   = floorIdRef.current;
        const tiles = roomMeshes.filter(m => m.userData.floorId === fid);
        const hits  = raycaster.intersectObjects(tiles, false);
        if (hits.length > 0) {
          const { floorId, roomId } = hits[0].object.userData;
          if (floorId !== undefined && roomId !== undefined) enterRoomCb.current(floorId, roomId);
        }
      }
    };
    container.addEventListener('pointerdown', onDown);
    container.addEventListener('pointerup',   onUp);

    // Pause when tab hidden
    let paused = false;
    const onVisibility = () => { paused = document.hidden; };
    document.addEventListener('visibilitychange', onVisibility);

    // Animation loop
    const camAnim    = camAnimRef.current;
    const EXPLODE_G  = 2.2;
    const animate    = () => {
      requestAnimationFrame(animate);
      if (paused) return;
      if (camAnim.active) {
        camera.position.lerp(camAnim.targetPos, 0.062);
        controls.target.lerp(camAnim.targetLookAt, 0.062);
        if (camera.position.distanceTo(camAnim.targetPos) < 0.2) {
          camera.position.copy(camAnim.targetPos);
          controls.target.copy(camAnim.targetLookAt);
          camAnim.active = false; controls.enabled = true;
        }
      }
      controls.update();

      floorGroups.forEach((grp, i) => {
        const tY = explodedRef.current ? grp.userData.originalY + i * EXPLODE_G : grp.userData.originalY;
        grp.position.y += (tY - grp.position.y) * 0.08;
        if (grp.userData.light) {
          const ti = explodedRef.current ? 2.5 : 0;
          grp.userData.light.intensity += (ti - grp.userData.light.intensity) * 0.1;
        }
        grp.children.forEach(c => {
          if (c.userData.isLabel)
            c.visible = modeRef.current === 'overview' || floorIdRef.current === grp.userData.floorId;
        });
      });

      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const W = container.clientWidth; const H = container.clientHeight;
      camera.aspect = W / H; camera.updateProjectionMatrix(); renderer.setSize(W, H);
    };
    window.addEventListener('resize', onResize);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', onResize);
      container.removeEventListener('pointerdown', onDown);
      container.removeEventListener('pointerup', onUp);
      controls.dispose(); renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, []);

  const fCol = currentFloor ? toHex(currentFloor.color) : '#38bdf8';
  const rCol = currentRoom  ? toHex(currentRoom.color)  : '#38bdf8';

  return (
    <div className="flex flex-col h-screen bg-dark-950 overflow-hidden">

      {/* Top bar */}
      <div className="flex-shrink-0 flex items-center justify-between px-5 h-14 border-b border-sky-400/10 bg-dark-950/95 backdrop-blur-sm z-20">
        <Link to="/" className="flex items-center gap-2 font-mono text-xs text-gray-400 hover:text-sky-400 transition-colors">
          <span>←</span>
          <span className="hidden sm:inline">Back to Portfolio</span>
        </Link>
        <span className="font-mono text-sm font-semibold">
          <span className="text-sky-400">{'<'}</span>
          <span className="text-white">3D Building</span>
          <span className="text-sky-400">{'/>'}</span>
        </span>
        <div className="flex gap-2">
          {mode !== 'overview' && (
            <button onClick={goBack} className="btn-tech text-xs px-3 py-1">← Back</button>
          )}
          {mode === 'overview' && (
            <button onClick={toggleExplode} className="btn-tech text-xs px-3 py-1">
              {isExploded ? 'Collapse' : 'Explode'}
            </button>
          )}
        </div>
      </div>

      {/* Canvas area */}
      <div className="flex-1 relative overflow-hidden">
        <div ref={containerRef} className="w-full h-full" />

        {/* Breadcrumb */}
        <div className="absolute top-4 left-4 flex items-center gap-1.5 font-mono text-xs flex-wrap z-10">
          <button
            onClick={mode !== 'overview' ? goBack : undefined}
            className={`px-2.5 py-1.5 rounded border transition-colors ${mode !== 'overview' ? 'border-sky-400/30 text-sky-400 hover:bg-sky-400/10 cursor-pointer' : 'border-sky-400/15 text-sky-400/50 cursor-default'}`}
          >
            🏢 Building
          </button>
          {currentFloor && (
            <>
              <span className="text-gray-600">/</span>
              <button
                onClick={mode === 'room' ? goBack : undefined}
                className={`px-2.5 py-1.5 rounded border transition-colors ${mode === 'room' ? 'cursor-pointer hover:bg-white/5' : 'cursor-default border-transparent'}`}
                style={{ color: fCol, borderColor: mode === 'room' ? `${fCol}44` : 'transparent' }}
              >
                {currentFloor.name}
              </button>
            </>
          )}
          {currentRoom && (
            <>
              <span className="text-gray-600">/</span>
              <span className="px-2.5 py-1.5 rounded border" style={{ color: rCol, borderColor: `${rCol}33` }}>
                {currentRoom.name}
              </span>
            </>
          )}
        </div>

        {/* Floor style legend (overview mode) */}
        {mode === 'overview' && (
          <div className="absolute top-4 right-4 glass-card p-3 rounded-lg z-10 hidden sm:block">
            <p className="font-mono text-xs text-gray-500 mb-2">// floor styles</p>
            {FLOORS.map((f, i) => (
              <div key={f.id} className="flex items-center gap-2 mb-1.5 last:mb-0">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: toHex(f.color) }} />
                <span className="font-mono text-xs" style={{ color: toHex(f.color) }}>{f.name}</span>
                <span className="text-gray-600 text-xs font-mono">— {SPECS[i].style}</span>
              </div>
            ))}
          </div>
        )}

        {/* Room selector — floor mode */}
        {currentFloor && mode === 'floor' && (
          <div className="absolute bottom-4 left-0 right-0 z-10 px-4 flex flex-col items-center gap-2">
            <span className="font-mono text-xs text-gray-500">
              <span style={{ color: fCol }}>{currentFloor.name}</span>
              {' '}— select a room
            </span>

            {/* Mobile: horizontal scroll strip */}
            <div className="flex sm:hidden gap-2 overflow-x-auto w-full pb-1 scrollbar-hide">
              {currentFloor.rooms.map(room => {
                const rc = toHex(room.color);
                return (
                  <button
                    key={room.id}
                    onClick={() => enterRoom(currentFloor.id, room.id)}
                    className="flex-shrink-0 flex items-center gap-2 glass-card px-3 py-2 rounded-lg transition-all active:scale-95"
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: rc }} />
                    <span className="font-mono text-xs font-semibold whitespace-nowrap" style={{ color: rc }}>{room.name}</span>
                    <span className="text-gray-600 text-xs">→</span>
                  </button>
                );
              })}
            </div>

            {/* Desktop: full cards grid */}
            <div className="hidden sm:grid sm:grid-cols-4 gap-2 w-full max-w-2xl">
              {currentFloor.rooms.map(room => {
                const rc = toHex(room.color);
                return (
                  <button
                    key={room.id}
                    onClick={() => enterRoom(currentFloor.id, room.id)}
                    className="glass-card p-3 rounded-lg text-left group transition-all hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: rc }} />
                        <span className="font-mono text-xs font-semibold truncate" style={{ color: rc }}>{room.name}</span>
                      </div>
                      <span className="text-gray-600 group-hover:text-sky-400 transition-colors text-xs flex-shrink-0 ml-1">→</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed line-clamp-2">{room.desc}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {room.items.slice(0, 2).map(item => (
                        <span key={item} className="tech-badge text-xs" style={{ color: rc, borderColor: `${rc}22` }}>{item}</span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Room info panel */}
        {currentRoom && mode === 'room' && (
          <div className="absolute bottom-16 right-4 glass-card p-4 rounded-lg w-64 z-10">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: rCol }} />
              <span className="font-mono text-xs text-gray-400">
                {'// '}{currentRoom.name.toLowerCase().replace(/\s+/g, '_')}
              </span>
            </div>
            <h3 className="text-white font-semibold text-sm mb-0.5">{currentRoom.name}</h3>
            <p className="font-mono text-xs mb-2" style={{ color: rCol }}>{currentFloor?.name}</p>
            <p className="text-gray-400 text-xs mb-3 leading-relaxed">{currentRoom.desc}</p>
            <div className="flex flex-wrap gap-1">
              {currentRoom.items.map(item => (
                <span key={item} className="tech-badge">{item}</span>
              ))}
            </div>
          </div>
        )}

        {/* Hint bar — overview and room mode only */}
        {showHint && mode !== 'floor' && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-dark-900/80 backdrop-blur-sm px-4 py-2 rounded-full border border-sky-400/10 text-xs text-gray-400 font-mono whitespace-nowrap z-10">
            {mode === 'overview' && <><span>Drag — orbit</span><span className="opacity-30">|</span><span>Scroll — zoom</span><span className="opacity-30">|</span><span>Click floor to enter</span></>}
            {mode === 'room'     && <><span>Orbit to explore room</span><span className="opacity-30">|</span><span>← Back to exit</span></>}
            <button className="ml-2 text-gray-600 hover:text-gray-300 transition-colors" onClick={() => setShowHint(false)}>✕</button>
          </div>
        )}
      </div>

      {/* Floor quick-nav (bottom bar) */}
      <div className="flex-shrink-0 flex items-center justify-center gap-2 px-4 py-2.5 border-t border-sky-400/10 bg-dark-950/80 backdrop-blur-sm z-20 flex-wrap">
        {FLOORS.map(f => (
          <button
            key={f.id}
            onClick={() => enterFloor(f.id)}
            className={`flex items-center gap-1.5 tech-badge hover:border-sky-400/40 transition-all text-xs ${currentFloor?.id === f.id ? 'border-sky-400/40 bg-sky-400/5' : ''}`}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: toHex(f.color) }} />
            {f.name}
          </button>
        ))}
      </div>
    </div>
  );
};

export default BuildingPage;
